/**
 * Turn Arbiter — draft turn accumulation with content-aware end-of-turn detection.
 *
 * Phase 1 introduced basic silence-based heuristics.
 * Phase 2 (#170) adds dynamic content-aware scoring:
 *
 * - **Continuation markers** ("but", "and", "however", "also", trailing conjunctions)
 *   → longer hold times, user is likely continuing
 * - **Ellipsis / trailing dots** → longer hold (user is thinking)
 * - **Unfinished lists** (numbered items, bullet points) → longer hold
 * - **Complete sentence fast path** (ends with .!? + short pause) → faster commit
 * - **Question endings** → slightly faster commit (user expects a response)
 *
 * Content scoring is additive to the base silence thresholds.
 * All thresholds are configurable via localStorage but hidden from the UI.
 *
 * Turn protocol integration:
 * - When TurnProtocolManager is provided, sends chat.turn.* frames
 * - Falls back gracefully when no protocol manager is set
 * - A protocol turn is only created when the first real STT segment arrives.
 *   VAD speech-start/end events are buffered until then so a pre-segment
 *   speech burst cannot leak an orphan turn.
 */

import { remoteLog } from '../utils/remoteLog';
import type { TurnProtocolManager } from './turnProtocol';

// ── Threshold Defaults ──

const DEFAULTS = {
  /** Max words for the "fast commit" path */
  FAST_COMMIT_MAX_WORDS: 4,
  /** Silence (ms) before fast-committing a short utterance */
  FAST_COMMIT_SILENCE_MS: 1200,
  /** Hold time (ms) for uncertain/longer utterances */
  UNCERTAIN_HOLD_MS: 2500,
  /** Absolute ceiling (ms) after last segment before forced commit */
  HARD_CEILING_MS: 5000,
  /** Grace period (ms) after an empty first STT result before cancelling the pending speech cycle. */
  MISFIRE_GRACE_MS: 400,
  /** Max time (ms) to wait for the first real STT segment after speech ends. */
  EMPTY_SEGMENT_TIMEOUT_MS: 1500,

  // ── Phase 2: Content-aware scoring additions ──

  /** Extra hold (ms) when trailing continuation marker detected */
  CONTINUATION_EXTRA_MS: 800,
  /** Extra hold (ms) when trailing ellipsis detected */
  ELLIPSIS_EXTRA_MS: 1000,
  /** Extra hold (ms) when unfinished list detected */
  LIST_EXTRA_MS: 600,
  /** Speedup factor for complete sentence ending (multiplied by base delay) */
  COMPLETE_SENTENCE_FACTOR: 0.6,
  /** Speedup factor for question endings */
  QUESTION_FACTOR: 0.7,
} as const;

// ── localStorage Keys ──

const LS_PREFIX = 'talkyn-arbiter-';
const LS_KEYS = {
  FAST_COMMIT_MAX_WORDS: `${LS_PREFIX}fast-words`,
  FAST_COMMIT_SILENCE_MS: `${LS_PREFIX}fast-silence-ms`,
  UNCERTAIN_HOLD_MS: `${LS_PREFIX}uncertain-hold-ms`,
  HARD_CEILING_MS: `${LS_PREFIX}hard-ceiling-ms`,
  CONTINUATION_EXTRA_MS: `${LS_PREFIX}continuation-extra-ms`,
  ELLIPSIS_EXTRA_MS: `${LS_PREFIX}ellipsis-extra-ms`,
  LIST_EXTRA_MS: `${LS_PREFIX}list-extra-ms`,
} as const;

/** Read an integer from localStorage with fallback */
function readInt(key: string, fallback: number): number {
  try {
    const v = localStorage.getItem(key);
    if (v !== null) {
      const n = parseInt(v, 10);
      if (!Number.isNaN(n) && n > 0) return n;
    }
  } catch {
    // ignore
  }
  return fallback;
}

// ── Content Analysis ──

/**
 * Continuation markers — words/phrases at the end of text that suggest
 * the speaker intends to continue. Case-insensitive matching.
 */
const CONTINUATION_MARKERS = new Set([
  'and', 'but', 'or', 'so', 'because', 'however', 'also',
  'although', 'though', 'while', 'whereas', 'plus',
  'moreover', 'furthermore', 'additionally', 'then',
  'first', 'second', 'third', 'next', 'finally',
  'for example', 'for instance', 'such as', 'like',
  'i mean', 'you know', 'basically', 'actually',
  'well', 'anyway', 'the',
]);

/** Two-word continuation markers (checked as trailing bigrams) */
const TWO_WORD_CONTINUATIONS = new Set([
  'for example', 'for instance', 'such as',
  'i mean', 'you know', 'and then', 'but also',
  'as well', 'in addition', 'on the',
]);

/**
 * Analyze the content of the accumulated draft for end-of-turn scoring hints.
 */
export interface ContentAnalysis {
  /** Text ends with a continuation marker */
  hasContinuation: boolean;
  /** Text ends with ellipsis (... or …) */
  hasEllipsis: boolean;
  /** Text appears to be an unfinished list */
  hasUnfinishedList: boolean;
  /** Text ends with a complete sentence (. ! ?) */
  isCompleteSentence: boolean;
  /** Text ends with a question mark */
  isQuestion: boolean;
  /** The detected marker (for logging) */
  marker?: string;
}

/**
 * Analyze accumulated text for content-aware end-of-turn scoring.
 */
export function analyzeContent(text: string): ContentAnalysis {
  const trimmed = text.trim();
  const lower = trimmed.toLowerCase();

  // Check for ellipsis
  const hasEllipsis = /\.{2,}\s*$/.test(trimmed) || /…\s*$/.test(trimmed);

  // Check complete sentence (ends with .!? possibly followed by quotes)
  const isCompleteSentence = /[.!?]["'»)]*\s*$/.test(trimmed) && !hasEllipsis;

  // Check question ending
  const isQuestion = /\?\s*["'»)]*\s*$/.test(trimmed);

  // Check for continuation markers
  let hasContinuation = false;
  let marker: string | undefined;

  // Check two-word continuations first
  const words = lower.split(/\s+/);
  if (words.length >= 2) {
    const lastTwo = words.slice(-2).join(' ');
    if (TWO_WORD_CONTINUATIONS.has(lastTwo)) {
      hasContinuation = true;
      marker = lastTwo;
    }
  }

  // Check single-word continuations
  if (!hasContinuation && words.length > 0) {
    const lastWord = words[words.length - 1].replace(/[,;:]+$/, '');
    if (CONTINUATION_MARKERS.has(lastWord)) {
      hasContinuation = true;
      marker = lastWord;
    }
  }

  // Check for trailing comma (often indicates continuation)
  if (!hasContinuation && /,\s*$/.test(trimmed)) {
    hasContinuation = true;
    marker = 'trailing_comma';
  }

  // Check for unfinished lists
  // Patterns: "1. ...\n2." or "- item\n-" or numbered items without a closing thought
  const hasUnfinishedList = detectUnfinishedList(trimmed);

  return {
    hasContinuation,
    hasEllipsis,
    hasUnfinishedList,
    isCompleteSentence,
    isQuestion,
    marker,
  };
}

/**
 * Detect if text contains an unfinished list.
 * Heuristic: if the text has list-like items and the last item looks incomplete.
 */
function detectUnfinishedList(text: string): boolean {
  const lines = text.split('\n');
  if (lines.length < 2) return false;

  // Count lines that look like list items
  let listItemCount = 0;
  for (const line of lines) {
    const trimmedLine = line.trim();
    if (/^\d+[.)]/.test(trimmedLine) || /^[-*•]/.test(trimmedLine)) {
      listItemCount++;
    }
  }

  // If we have list items, check if the last line is incomplete
  if (listItemCount >= 1) {
    const lastLine = lines[lines.length - 1].trim();
    // Last line is a list item but very short (< 20 chars) or doesn't end with punctuation
    if (lastLine.length > 0 && lastLine.length < 20 && !/[.!?]$/.test(lastLine)) {
      return true;
    }
    // Last line starts a new list item but is otherwise empty
    if (/^\d+[.)]\s*$/.test(lastLine) || /^[-*•]\s*$/.test(lastLine)) {
      return true;
    }
  }

  return false;
}

// ── Types ──

export interface TurnArbiterCallbacks {
  /** Called when the draft turn is committed — consumer should fire chat.send */
  onCommit: (fullTranscript: string) => void;
  /** Called when the draft is cancelled (misfire cleanup or explicit cancel) */
  onCancel: (meta: TurnCancelMeta) => void;
  /** Called whenever the draft text changes (for UI updates) */
  onDraftUpdate: (draftText: string) => void;
}

export interface PitchHint {
  /** Whether pitch was falling at the end of the segment (prosodic cue for statement) */
  falling: boolean;
  /** Confidence 0–1 (0 = not available / not reliable) */
  confidence: number;
}

export type ArbiterState = 'idle' | 'accumulating' | 'committing';

export interface TurnCancelMeta {
  /** Why the turn was cancelled */
  reason: string;
  /** Whether any real transcript content had been accumulated */
  hadDraft: boolean;
  /** Whether deferred TTS should be resumed/spoken after the cancel */
  shouldFlushDeferredTts: boolean;
}

export interface ForceCancelOptions {
  /** Whether deferred TTS should be resumed/spoken after the cancel */
  flushDeferredTts?: boolean;
}

// ── Turn Arbiter ──

export class TurnArbiter {
  private callbacks: TurnArbiterCallbacks;

  // Draft state
  private segments: string[] = [];
  private _state: ArbiterState = 'idle';
  private lastSegmentTime = 0;

  // Turn protocol
  private protocol: TurnProtocolManager | null = null;
  private currentTurnId: string | null = null;
  private pendingSpeechStartTs: number | null = null;
  private pendingSpeechEndTs: number | null = null;
  private awaitingFirstSegment = false;

  // Timers
  private commitTimer: ReturnType<typeof setTimeout> | null = null;
  private hardCeilingTimer: ReturnType<typeof setTimeout> | null = null;
  private firstSegmentTimer: ReturnType<typeof setTimeout> | null = null;

  // Thresholds (read once on construction, stable for session)
  private readonly fastMaxWords: number;
  private readonly fastSilenceMs: number;
  private readonly uncertainHoldMs: number;
  private readonly hardCeilingMs: number;
  private readonly continuationExtraMs: number;
  private readonly ellipsisExtraMs: number;
  private readonly listExtraMs: number;
  private readonly misfireGraceMs = DEFAULTS.MISFIRE_GRACE_MS;
  private readonly emptySegmentTimeoutMs = DEFAULTS.EMPTY_SEGMENT_TIMEOUT_MS;

  constructor(callbacks: TurnArbiterCallbacks) {
    this.callbacks = callbacks;
    this.fastMaxWords = readInt(LS_KEYS.FAST_COMMIT_MAX_WORDS, DEFAULTS.FAST_COMMIT_MAX_WORDS);
    this.fastSilenceMs = readInt(LS_KEYS.FAST_COMMIT_SILENCE_MS, DEFAULTS.FAST_COMMIT_SILENCE_MS);
    this.uncertainHoldMs = readInt(LS_KEYS.UNCERTAIN_HOLD_MS, DEFAULTS.UNCERTAIN_HOLD_MS);
    this.hardCeilingMs = readInt(LS_KEYS.HARD_CEILING_MS, DEFAULTS.HARD_CEILING_MS);
    this.continuationExtraMs = readInt(LS_KEYS.CONTINUATION_EXTRA_MS, DEFAULTS.CONTINUATION_EXTRA_MS);
    this.ellipsisExtraMs = readInt(LS_KEYS.ELLIPSIS_EXTRA_MS, DEFAULTS.ELLIPSIS_EXTRA_MS);
    this.listExtraMs = readInt(LS_KEYS.LIST_EXTRA_MS, DEFAULTS.LIST_EXTRA_MS);
  }

  /** Current arbiter state */
  get state(): ArbiterState {
    return this._state;
  }

  /** Current accumulated draft text */
  get draftText(): string {
    return this.segments.join(' ').trim();
  }

  /** Whether the arbiter has an active draft turn */
  get isAccumulating(): boolean {
    return this._state === 'accumulating';
  }

  /** Whether the arbiter is tracking a speech cycle that hasn't resolved yet. */
  get hasPendingTurn(): boolean {
    return this.awaitingFirstSegment || this.segments.length > 0 || this.currentTurnId !== null;
  }

  /** Current turn ID (if active) */
  get turnId(): string | null {
    return this.currentTurnId;
  }

  /**
   * Set the turn protocol manager (optional — noop fallback if not set).
   */
  setProtocol(protocol: TurnProtocolManager | null): void {
    this.protocol = protocol;
  }

  /**
   * Feed a new STT transcript segment into the draft turn.
   *
   * @param text - Transcribed text for this speech segment
   * @param pitchHint - Optional prosodic hint (Phase 1: not wired, always omit)
   */
  addSegment(text: string, pitchHint?: PitchHint): void {
    const trimmed = text.trim();

    // Empty segment handling — misfires, gate rejections, empty STT results
    if (!trimmed) {
      this.handleEmptySegment();
      return;
    }

    this.clearFirstSegmentTimer();

    // Transition to accumulating and open the protocol turn only once we have
    // a real transcript segment. Any buffered speech-start/end updates are
    // flushed onto the new turn before the first append.
    if (this._state === 'idle') {
      this._state = 'accumulating';
      this.ensureProtocolTurn();
    }

    this.segments.push(trimmed);
    this.lastSegmentTime = Date.now();
    this.callbacks.onDraftUpdate(this.draftText);

    // Send segment to protocol
    if (this.currentTurnId) {
      this.protocol?.sendAppend(this.currentTurnId, trimmed, this.segments.length - 1);
    }

    remoteLog('arbiter', 'segment_added', {
      text: trimmed.substring(0, 60),
      segmentCount: this.segments.length,
      totalWords: this.wordCount(),
      turnId: this.currentTurnId,
    });

    // Restart commit scoring
    this.scheduleCommit(pitchHint);
  }

  /**
   * Notify the arbiter that new speech has started (VAD onSpeechStart).
   * Cancels all pending commit timers since the user is still talking.
   *
   * A protocol turn is NOT opened here. We only buffer the speech-start event
   * until the first real segment arrives, which avoids leaking orphan turns
   * when the user produces a misfire or cancels before STT yields content.
   */
  onSpeechStart(): void {
    this.clearCommitTimers();
    this.clearFirstSegmentTimer();

    const ts = Date.now();

    if (this.currentTurnId) {
      this.protocol?.sendUpdate(this.currentTurnId, 'speech_start', ts);
      return;
    }

    this.awaitingFirstSegment = true;
    this.pendingSpeechStartTs = ts;
    this.pendingSpeechEndTs = null;

    remoteLog('arbiter', 'speech_start_pending', { ts });
  }

  /**
   * Notify the arbiter that speech ended (VAD onSpeechEnd).
   * Used for protocol tracking and to bound how long we'll wait for the first
   * STT segment before treating the speech burst as a misfire.
   */
  onSpeechEnd(): void {
    const ts = Date.now();

    if (this.currentTurnId) {
      this.protocol?.sendUpdate(this.currentTurnId, 'speech_end', ts);
      return;
    }

    if (!this.awaitingFirstSegment) {
      return;
    }

    this.pendingSpeechEndTs = ts;
    remoteLog('arbiter', 'speech_end_pending', { ts });
    this.scheduleFirstSegmentCancel(this.emptySegmentTimeoutMs, 'speech_end_no_segment');
  }

  /**
   * Force-commit the current draft (e.g. user taps orb while accumulating).
   * No-op if there's nothing to commit.
   */
  forceCommit(): void {
    this.clearFirstSegmentTimer();
    if (this.segments.length === 0) return;
    this.doCommit('force');
  }

  /**
   * Force-cancel the current draft or pending speech cycle.
   */
  forceCancel(
    reason: string = 'force_cancel',
    options?: ForceCancelOptions,
  ): void {
    if (!this.hasPendingTurn) return;

    const hadDraft = this.segments.length > 0;

    remoteLog('arbiter', 'force_cancel', {
      reason,
      hadDraft,
      segmentCount: this.segments.length,
      turnId: this.currentTurnId,
    });

    if (this.currentTurnId) {
      this.protocol?.sendCancel(this.currentTurnId, reason);
    }

    this.finishCancel({
      reason,
      hadDraft,
      shouldFlushDeferredTts: options?.flushDeferredTts ?? false,
    });
  }

  /**
   * Clean up timers. Call when the arbiter is no longer needed.
   */
  destroy(): void {
    this.reset();
  }

  // ── Private ──

  private wordCount(): number {
    return this.draftText.split(/\s+/).filter(Boolean).length;
  }

  private handleEmptySegment(): void {
    remoteLog('arbiter', 'empty_segment', {
      segmentCount: this.segments.length,
      state: this._state,
      awaitingFirstSegment: this.awaitingFirstSegment,
      turnId: this.currentTurnId,
    });

    if (this.segments.length === 0) {
      if (this.awaitingFirstSegment) {
        // We saw speech but still have no transcript. Give a short grace period
        // for a late real segment before treating this as a misfire.
        this.scheduleFirstSegmentCancel(this.misfireGraceMs, 'empty_no_draft');
        return;
      }

      // Stray empty result with no active draft — cancel immediately.
      this.finishCancel({
        reason: 'empty_no_draft',
        hadDraft: false,
        shouldFlushDeferredTts: true,
      });
      return;
    }

    // Has existing draft — re-schedule commit timer.
    remoteLog('arbiter', 'empty_segment_reschedule', { segmentCount: this.segments.length });
    this.scheduleCommit();
  }

  private ensureProtocolTurn(): void {
    if (this.currentTurnId) {
      return;
    }

    if (!this.protocol?.enabled) {
      this.clearPendingSpeechTracking();
      return;
    }

    this.currentTurnId = this.protocol.generateTurnId();
    this.protocol.sendStart(this.currentTurnId);
    this.flushPendingSpeechUpdates();
  }

  private flushPendingSpeechUpdates(): void {
    if (!this.currentTurnId) {
      this.clearPendingSpeechTracking();
      return;
    }

    if (this.pendingSpeechStartTs !== null) {
      this.protocol?.sendUpdate(this.currentTurnId, 'speech_start', this.pendingSpeechStartTs);
    }
    if (this.pendingSpeechEndTs !== null) {
      this.protocol?.sendUpdate(this.currentTurnId, 'speech_end', this.pendingSpeechEndTs);
    }

    this.clearPendingSpeechTracking();
  }

  private scheduleFirstSegmentCancel(delayMs: number, reason: string): void {
    this.clearFirstSegmentTimer();

    this.firstSegmentTimer = setTimeout(() => {
      this.firstSegmentTimer = null;

      if (!this.awaitingFirstSegment || this.segments.length > 0 || this.currentTurnId) {
        return;
      }

      remoteLog('arbiter', 'cancel_pending_speech', { reason, delayMs });
      this.finishCancel({
        reason,
        hadDraft: false,
        shouldFlushDeferredTts: true,
      });
    }, delayMs);
  }

  private scheduleCommit(pitchHint?: PitchHint): void {
    // Clear existing commit timer (will be rescheduled)
    if (this.commitTimer) {
      clearTimeout(this.commitTimer);
      this.commitTimer = null;
    }

    const words = this.wordCount();
    const hasFallingPitch = pitchHint?.falling && (pitchHint.confidence ?? 0) > 0.5;

    // ── Phase 2: Content-aware analysis ──
    const analysis = analyzeContent(this.draftText);

    let delayMs: number;
    let path: string;

    if (words <= this.fastMaxWords) {
      // Fast path: short utterance
      if (hasFallingPitch) {
        delayMs = Math.round(this.fastSilenceMs * 0.6);
        path = 'fast+pitch';
      } else {
        delayMs = this.fastSilenceMs;
        path = 'fast';
      }
    } else {
      // Uncertain path: longer utterance, wait for more context
      if (hasFallingPitch) {
        delayMs = Math.round(this.uncertainHoldMs * 0.7);
        path = 'uncertain+pitch';
      } else {
        delayMs = this.uncertainHoldMs;
        path = 'uncertain';
      }
    }

    // ── Apply content-aware modifiers ──

    // Complete sentence fast path — reduce delay
    if (analysis.isCompleteSentence && words > this.fastMaxWords) {
      delayMs = Math.round(delayMs * DEFAULTS.COMPLETE_SENTENCE_FACTOR);
      path += '+complete';
    }

    // Question ending — slightly faster commit
    if (analysis.isQuestion && words > this.fastMaxWords) {
      delayMs = Math.round(delayMs * DEFAULTS.QUESTION_FACTOR);
      path += '+question';
    }

    // Continuation markers — extend delay
    if (analysis.hasContinuation) {
      delayMs += this.continuationExtraMs;
      path += `+continuation(${analysis.marker})`;
    }

    // Ellipsis — extend delay (user is thinking)
    if (analysis.hasEllipsis) {
      delayMs += this.ellipsisExtraMs;
      path += '+ellipsis';
    }

    // Unfinished list — extend delay
    if (analysis.hasUnfinishedList) {
      delayMs += this.listExtraMs;
      path += '+list';
    }

    remoteLog('arbiter', 'schedule_commit', {
      path,
      delayMs,
      words,
      analysis: {
        continuation: analysis.hasContinuation,
        ellipsis: analysis.hasEllipsis,
        list: analysis.hasUnfinishedList,
        complete: analysis.isCompleteSentence,
        question: analysis.isQuestion,
      },
    });

    this.commitTimer = setTimeout(() => {
      this.doCommit(path);
    }, delayMs);

    // Reset hard ceiling timer on EVERY segment
    if (this.hardCeilingTimer) clearTimeout(this.hardCeilingTimer);
    this.hardCeilingTimer = setTimeout(() => {
      if (this._state === 'accumulating' && this.segments.length > 0) {
        this.doCommit('hard_ceiling');
      }
    }, this.hardCeilingMs);
  }

  private doCommit(reason: string): void {
    const text = this.draftText;
    if (!text) {
      if (this.currentTurnId) {
        this.protocol?.sendCancel(this.currentTurnId, 'empty_commit');
      }
      this.finishCancel({
        reason: 'empty_commit',
        hadDraft: false,
        shouldFlushDeferredTts: true,
      });
      return;
    }

    this._state = 'committing';

    // Send protocol commit
    if (this.currentTurnId) {
      this.protocol?.sendCommit(
        this.currentTurnId,
        text,
        this.segments.length,
        reason,
      );
    }

    remoteLog('arbiter', 'commit', {
      reason,
      text: text.substring(0, 80),
      segmentCount: this.segments.length,
      totalWords: this.wordCount(),
      durationMs: this.lastSegmentTime > 0 ? Date.now() - this.lastSegmentTime : 0,
      turnId: this.currentTurnId,
    });

    const finalText = text;
    this.reset();
    this.callbacks.onCommit(finalText);
  }

  private finishCancel(meta: TurnCancelMeta): void {
    this.reset();
    this.callbacks.onCancel(meta);
  }

  private reset(): void {
    this.clearAllTimers();
    this.segments = [];
    this._state = 'idle';
    this.lastSegmentTime = 0;
    this.currentTurnId = null;
    this.pendingSpeechStartTs = null;
    this.pendingSpeechEndTs = null;
    this.awaitingFirstSegment = false;
  }

  private clearAllTimers(): void {
    this.clearCommitTimers();
    this.clearFirstSegmentTimer();
  }

  private clearCommitTimers(): void {
    if (this.commitTimer) {
      clearTimeout(this.commitTimer);
      this.commitTimer = null;
    }
    if (this.hardCeilingTimer) {
      clearTimeout(this.hardCeilingTimer);
      this.hardCeilingTimer = null;
    }
  }

  private clearFirstSegmentTimer(): void {
    if (this.firstSegmentTimer) {
      clearTimeout(this.firstSegmentTimer);
      this.firstSegmentTimer = null;
    }
  }

  private clearPendingSpeechTracking(): void {
    this.clearFirstSegmentTimer();
    this.awaitingFirstSegment = false;
    this.pendingSpeechStartTs = null;
    this.pendingSpeechEndTs = null;
  }
}
